import { Link, useLocation, useNavigate } from "react-router-dom";
import { useAuth } from "@/context/AuthContext";
import { useEffect } from "react";
import {
  LayoutDashboard,
  Home,
  Star,
  MessageSquare,
  LogOut,
  Menu,
  X,
  MapPin,
  Building,
  Compass,
  FileText,
  Settings,
  Database,
  BarChart3,
  Search,
  ArrowRightLeft,
  ToggleLeft,
  Globe,
  UserCircle,
  Award,
  Image as ImageIcon,
} from "lucide-react";
import { Button } from "@/components/ui/button";
import { useState } from "react";

export const AdminLayout = ({ children }) => {
  const { isAuthenticated, logout } = useAuth();
  const navigate = useNavigate();
  const location = useLocation();
  const [sidebarOpen, setSidebarOpen] = useState(false);

  useEffect(() => {
    if (!isAuthenticated) {
      navigate("/admin/login");
    }
  }, [isAuthenticated, navigate]);

  const handleLogout = () => {
    logout();
    navigate("/admin/login");
  };

  const menuItems = [
    { path: "/admin", label: "Dashboard", icon: LayoutDashboard },
    { path: "/admin/analytics", label: "Analytics", icon: BarChart3 },
    { path: "/admin/homestays", label: "Homestays", icon: Home },
    { path: "/admin/locations", label: "Locations", icon: MapPin },
    { path: "/admin/villages", label: "Villages", icon: Building },
    { path: "/admin/places", label: "Places", icon: Compass },
    { path: "/admin/blog", label: "Blog", icon: FileText },
    { path: "/admin/authors", label: "Authors", icon: UserCircle },
    { path: "/admin/featured-on", label: "Featured On", icon: Award },
    { path: "/admin/reviews", label: "Reviews", icon: Star },
    { path: "/admin/place-reviews", label: "Place Reviews", icon: MessageSquare },
    { path: "/admin/inquiries", label: "Inquiries", icon: MessageSquare },
    { path: "/admin/seo", label: "Programmatic SEO", icon: Search },
    { path: "/admin/seo-collections", label: "SEO Collections", icon: Globe },
    { path: "/admin/seo-import", label: "SEO Import", icon: Globe },
    { path: "/admin/redirects", label: "URL Redirects", icon: ArrowRightLeft },
    { path: "/admin/features", label: "Feature Flags", icon: ToggleLeft },
    { path: "/admin/media", label: "Media Uploads", icon: ImageIcon },
    { path: "/admin/settings", label: "Settings", icon: Settings },
    { path: "/admin/migration", label: "Data Migration", icon: Database },
  ];

  const isActive = (path) => location.pathname === path;

  if (!isAuthenticated) {
    return null;
  }

  return (
    <div className="flex min-h-screen bg-muted" data-testid="admin-layout">
      {/* Sidebar */}
      <aside
        className={`fixed inset-y-0 left-0 z-50 w-64 admin-sidebar transform transition-transform duration-200 ease-in-out ${
          sidebarOpen ? "translate-x-0" : "-translate-x-full"
        } lg:translate-x-0 lg:static`}
        data-testid="admin-sidebar"
      >
        <div className="flex flex-col h-full p-6">
          {/* Logo */}
          <div className="flex items-center justify-between mb-8">
            <Link to="/admin" className="flex items-center gap-2">
              <Home className="w-7 h-7 text-accent" />
              <span className="text-xl font-semibold">
                Kokan Homestays
              </span>
            </Link>
            <button
              className="lg:hidden text-white"
              onClick={() => setSidebarOpen(false)}
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          {/* Menu */}
          <nav className="flex-1 space-y-2">
            {menuItems.map((item) => (
              <Link
                key={item.path}
                to={item.path}
                className={`flex items-center gap-3 px-4 py-3 rounded-xl admin-sidebar-item ${
                  isActive(item.path) ? "active" : ""
                }`}
                onClick={() => setSidebarOpen(false)}
                data-testid={`admin-nav-${item.label.toLowerCase()}`}
              >
                <item.icon className="w-5 h-5" />
                <span className="font-medium">{item.label}</span>
              </Link>
            ))}
          </nav>

          {/* Logout */}
          <div className="pt-4 border-t border-gray-700">
            <button
              onClick={handleLogout}
              className="flex items-center gap-3 px-4 py-3 w-full rounded-xl admin-sidebar-item text-red-400 hover:text-red-300"
              data-testid="admin-logout"
            >
              <LogOut className="w-5 h-5" />
              <span className="font-medium">Logout</span>
            </button>
            <Link
              to="/"
              className="flex items-center gap-3 px-4 py-3 mt-2 rounded-xl admin-sidebar-item text-gray-400 hover:text-white"
              data-testid="admin-view-site"
            >
              <Home className="w-5 h-5" />
              <span className="font-medium">View Site</span>
            </Link>
          </div>
        </div>
      </aside>

      {/* Main Content */}
      <div className="flex-1 flex flex-col min-h-screen lg:ml-0 min-w-0 overflow-x-hidden">
        {/* Mobile Header */}
        <header className="lg:hidden flex items-center justify-between p-4 bg-white border-b">
          <button
            onClick={() => setSidebarOpen(true)}
            className="p-2"
            data-testid="admin-menu-toggle"
          >
            <Menu className="w-6 h-6" />
          </button>
          <span className="font-semibold">
            Admin Panel
          </span>
          <div className="w-10" />
        </header>

        {/* Content */}
        <main className="flex-1 p-4 sm:p-6 lg:p-8 overflow-x-hidden">{children}</main>
      </div>

      {/* Overlay */}
      {sidebarOpen && (
        <div
          className="fixed inset-0 bg-black/50 z-40 lg:hidden"
          onClick={() => setSidebarOpen(false)}
        />
      )}
    </div>
  );
};

export default AdminLayout;
